import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  DatePicker,
  Button,
  Tag,
  Grid,
  Input,
  Select,
  Segmented,
  Space,
  Modal,
  Descriptions,
  Typography,
  Spin,
} from "antd";
import {
  DownloadOutlined,
  CopyOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilterOutlined,
  FileOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { useUserOrg } from "shared/contexts/UserOrgContext";
import { mcapLogsService } from "./services/mcapLogsService";
import { getApiErrorMessage } from "./api-error";
import { formatDateTime } from "shared/utils/datetime";
import { BUTTON_STYLES } from "features/administration/constants";
import createLogColumns from "./components/logTableColumns";
import LogRowDetail from "./components/LogRowDetail";
import { ProcessingStateTag, UploadStateTag } from "./components/logStateTags";
import { formatBytes, formatDuration } from "./utils/formatters";
import {
  DEFAULT_DENSITY,
  DEFAULT_SORT_BY,
  DEFAULT_SORT_ORDER,
  DENSITY_OPTIONS,
  DENSITY_STORAGE_KEY,
  PAGE_SIZE_OPTIONS,
  PROCESSING_STATE_OPTIONS,
  RECORDING_STATE_OPTIONS,
  ROBOT_LOGS_DASHBOARD_PATH,
  SORT_FIELDS,
  UPLOAD_STATE_OPTIONS,
  isReachable,
} from "./utils/logConstants";
import {
  TIME_WINDOW_KEYS,
  buildSearch,
  parseQuery,
  toApiSortOrder,
} from "./utils/logQuery";
import {
  WINDOW_PICKER_PROPS,
  boundsToWindow,
  buildWindowPresets,
  windowToBounds,
} from "./utils/windowPicker";
import "./robot-logs.css";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;

// Both time windows share the picker setup and quick look-backs of
// utils/windowPicker.js with the dashboard (this page has no i18n, so the
// presets keep their English labels).
const WINDOW_PRESETS = buildWindowPresets();

const readStoredDensity = () => {
  try {
    const stored = localStorage.getItem(DENSITY_STORAGE_KEY);
    return DENSITY_OPTIONS.some((o) => o.value === stored) ? stored : DEFAULT_DENSITY;
  } catch {
    // Private-mode / disabled storage: fall back rather than break the page.
    return DEFAULT_DENSITY;
  }
};

const RaasRobotLogsPage = () => {
  const { authFetch, loading: authLoading, isAuthenticated } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { selectedOrg } = useUserOrg();
  const history = useHistory();
  const location = useLocation();
  const screens = useBreakpoint();

  // `scroll.x` is a MINIMUM table width, so it has to shrink in step with the
  // columns that `responsive` drops — otherwise a phone scrolls sideways to
  // reach whitespace where the hidden columns used to be. Before the media
  // queries resolve `screens` is empty; assume desktop so the layout doesn't
  // flash the narrow variant.
  const breakpointsResolved = Object.keys(screens).length > 0;
  const isNarrow = breakpointsResolved && !screens.sm;
  // Widths track the eight columns in components/logTableColumns.jsx and the
  // breakpoint each one drops at: Topics (xxl), Tasks + Size (xl), Times (lg),
  // Status (md), Robot (sm); File and Actions always render.
  const scrollX =
    !breakpointsResolved || screens.xxl
      ? 1400
      : screens.xl
      ? 1270
      : screens.lg
      ? 980
      : screens.md
      ? 800
      : screens.sm
      ? 600
      : 340;

  // URL is the source of truth for filters + pagination + sort.
  const query = useMemo(() => parseQuery(location.search), [location.search]);

  // State management
  const [logsData, setLogsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [density, setDensity] = useState(readStoredDensity);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  // Text inputs are local state mirroring the URL (committed on search /
  // debounce), re-synced when the URL changes externally (Back/Forward,
  // shared link).
  const [searchText, setSearchText] = useState(query.robot_id || "");
  const [locationText, setLocationText] = useState(query.location || "");

  useEffect(() => {
    setSearchText(query.robot_id || "");
    setLocationText(query.location || "");
  }, [query.robot_id, query.location]);

  // Task and scene-type names seen across the org's files, offered as
  // suggestions in the Tasks / Scene Types filters. They come from the stats
  // aggregation, which is not free, so they load the first time either
  // dropdown opens and are cached per org; both Selects accept free text
  // regardless, so a failed load only costs the hints.
  const [suggestions, setSuggestions] = useState({ orgId: null, tasks: [], sceneTypes: [] });
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Detail modal
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Guards against out-of-order detail responses (open A, quickly open B).
  const detailRequestIdRef = useRef(0);

  // Download modal
  const [downloadModal, setDownloadModal] = useState({ visible: false, url: "", filename: "" });

  const navigateQuery = (nextQuery, { replace = false } = {}) => {
    const search = buildSearch(nextQuery);
    if (replace) {
      history.replace({ search });
    } else {
      history.push({ search });
    }
  };

  /**
   * Load MCAP logs whenever the URL query, org, or refreshKey changes.
   */
  useEffect(() => {
    if (authLoading) return undefined;
    if (!isAuthenticated) {
      notifyError("You must be logged in to view this page.");
      setLoading(false);
      return undefined;
    }
    const orgId = selectedOrg?.organization?.org_id;
    if (!orgId) {
      setLogsData([]);
      setTotal(0);
      setLoading(false);
      return undefined;
    }

    let ignore = false;
    const fetchLogs = async () => {
      setLoading(true);
      try {
        const params = {
          skip: (query.page - 1) * query.pageSize,
          limit: query.pageSize,
          org_id: orgId,
          robot_id: query.robot_id,
          location: query.location,
          upload_state: query.upload_state,
          processing_state: query.processing_state,
          recording_state: query.recording_state,
          tasks: query.tasks,
          scene_types: query.scene_types,
          uploaded_from: query.uploaded_from,
          uploaded_to: query.uploaded_to,
          recorded_from: query.recorded_from,
          recorded_to: query.recorded_to,
          sort_by: query.sortBy,
          sort_order: toApiSortOrder(query.sortOrder),
        };
        const response = await mcapLogsService.getAll(authFetch, params);
        if (ignore) return;
        setLogsData(response.data.items || []);
        setTotal(response.data.total || 0);
      } catch (error) {
        if (ignore) return;
        console.error("Error fetching MCAP logs:", error);
        notifyError("Failed to fetch MCAP logs: " + getApiErrorMessage(error));
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchLogs();
    return () => {
      ignore = true;
    };
    // authFetch/notifyError are not referentially stable and must not
    // re-trigger fetches, so they are deliberately left out of the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, selectedOrg, query, refreshKey]);

  // Debounced live filtering for the Location input; replace (not push) so
  // Back/Forward doesn't step through keystrokes.
  useEffect(() => {
    if (locationText === (query.location || "")) return undefined;
    const timer = setTimeout(() => {
      navigateQuery(
        { ...query, location: locationText || undefined, page: 1 },
        { replace: true },
      );
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationText, query]);

  /**
   * Load detailed log information
   */
  const loadLogDetail = async (logId) => {
    const requestId = ++detailRequestIdRef.current;
    try {
      setDetailLoading(true);
      const response = await mcapLogsService.getById(authFetch, logId);
      if (requestId !== detailRequestIdRef.current) return;
      setSelectedLog(response.data);
    } catch (error) {
      if (requestId !== detailRequestIdRef.current) return;
      console.error("Error fetching log details:", error);
      notifyError("Failed to fetch log details: " + getApiErrorMessage(error));
    } finally {
      if (requestId === detailRequestIdRef.current) setDetailLoading(false);
    }
  };

  /**
   * Handle view details
   */
  const handleViewDetails = (record) => {
    setIsDetailModalVisible(true);
    setSelectedLog(null);
    loadLogDetail(record._id);
  };

  /**
   * Handle download
   */
  const handleDownload = async (record) => {
    if (!isReachable(record)) {
      notifyError(
        "This file is stored on the organization's private storage server and cannot be downloaded from here."
      );
      return;
    }
    try {
      const presignedUrlResponse = await mcapLogsService.getPresignedDownloadUrl(
        authFetch,
        record._id
      );
      if (presignedUrlResponse && presignedUrlResponse.presigned_url) {
        const url = presignedUrlResponse.presigned_url;
        const filename = presignedUrlResponse.filename || record.filename;

        setDownloadModal({ visible: true, url, filename });
      } else {
        notifyError("Failed to get download URL");
      }
    } catch (error) {
      const errorMessage = getApiErrorMessage(error);
      notifyError("Failed to download file: " + errorMessage);
    }
  };

  /**
   * Handle delete
   */
  const handleDelete = (record) => {
    const isLastRowOnPage = logsData.length === 1 && query.page > 1;
    Modal.confirm({
      title: "Delete MCAP Log File",
      content: `Are you sure you want to delete "${record.filename}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await mcapLogsService.delete(authFetch, record._id);
          notifySuccess("Log file deleted successfully");
          if (isLastRowOnPage) {
            navigateQuery({ ...query, page: query.page - 1 }, { replace: true });
          } else {
            setRefreshKey((key) => key + 1);
          }
        } catch (error) {
          console.error("Error deleting log file:", error);
          notifyError("Failed to delete log file: " + getApiErrorMessage(error));
        }
      },
    });
  };

  /**
   * Handle table change (pagination + sorting).
   *
   * Sorting is server-side, so a sort change invalidates the current offset —
   * page 3 of the old ordering has nothing to do with page 3 of the new one.
   * Reset to page 1 whenever the sort key or direction moves.
   */
  const handleTableChange = (pagination, _filters, sorter) => {
    const active = Array.isArray(sorter) ? sorter[0] : sorter;
    // AntD clears `order` on the third header click; fall back to the default
    // ordering rather than asking the backend for an unordered listing.
    const hasOrder = Boolean(active?.order);
    const nextSortBy =
      hasOrder && SORT_FIELDS.includes(active.columnKey)
        ? active.columnKey
        : DEFAULT_SORT_BY;
    const nextSortOrder = hasOrder ? active.order : DEFAULT_SORT_ORDER;
    const sortChanged =
      nextSortBy !== query.sortBy || nextSortOrder !== query.sortOrder;

    navigateQuery({
      ...query,
      sortBy: nextSortBy,
      sortOrder: nextSortOrder,
      page: sortChanged ? 1 : pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  /**
   * Handle table density change; persisted so the choice survives reloads.
   */
  const handleDensityChange = (value) => {
    setDensity(value);
    try {
      localStorage.setItem(DENSITY_STORAGE_KEY, value);
    } catch {
      // Non-fatal: the toggle still applies for this session.
    }
  };

  /**
   * Build the change handler for one lifecycle-state filter (upload,
   * processing or recording state); clearing the Select removes the filter.
   */
  const handleStateChange = (key) => (value) => {
    navigateQuery({ ...query, [key]: value || undefined, page: 1 });
  };

  /**
   * Build the change handler for one name-list filter (tasks or scene types);
   * a file matches when it carries ANY of the names.
   */
  const handleNamesChange = (key) => (values) => {
    const names = [...new Set(values.map((name) => name.trim()).filter(Boolean))];
    navigateQuery({ ...query, [key]: names.length > 0 ? names : undefined, page: 1 });
  };

  /**
   * Load task / scene-type suggestions for the current org the first time
   * either dropdown opens.
   */
  const loadSuggestions = async () => {
    const orgId = selectedOrg?.organization?.org_id;
    if (!orgId || suggestions.orgId === orgId || suggestionsLoading) return;
    setSuggestionsLoading(true);
    try {
      const response = await mcapLogsService.getStats(authFetch, { org_id: orgId });
      const toOptions = (counts) =>
        Object.keys(counts || {})
          .sort()
          .map((name) => ({ value: name, label: name }));
      setSuggestions({
        orgId,
        tasks: toOptions(response.data?.files_by_task),
        sceneTypes: toOptions(response.data?.files_by_scene_type),
      });
    } catch (error) {
      console.warn("Filter suggestions unavailable:", error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  /**
   * Build the change handler for one time window (upload or recording); the
   * picker's local-time dates become UTC instants in the URL.
   */
  const handleWindowChange = (fromKey, toKey) => (dates) => {
    const { from, to } = windowToBounds(dates);
    navigateQuery({ ...query, [fromKey]: from, [toKey]: to, page: 1 });
  };

  /** Picker value for one time window, derived from the URL (null when unset). */
  const windowValue = (fromKey, toKey) => boundsToWindow(query[fromKey], query[toKey]);

  /** The dashboard shows the same window as this list when one is set. */
  const openDashboard = () => {
    const timeWindow = Object.fromEntries(TIME_WINDOW_KEYS.map((key) => [key, query[key]]));
    history.push({ pathname: ROBOT_LOGS_DASHBOARD_PATH, search: buildSearch(timeWindow) });
  };

  /**
   * Handle search
   */
  const handleSearch = () => {
    navigateQuery({ ...query, robot_id: searchText || undefined, page: 1 });
  };

  /**
   * Handle reset filters
   */
  const handleResetFilters = () => {
    setSearchText("");
    setLocationText("");
    history.push({ search: "" });
  };

  // Seven grouped columns replace the previous thirteen flat ones; see
  // components/logTableColumns.jsx for the layout rationale. Rebuilt every
  // render on purpose: the action handlers close over `logsData` and `query`,
  // and memoising them here would only invite stale-closure bugs.
  const columns = createLogColumns({
    onView: handleViewDetails,
    onDownload: handleDownload,
    onDelete: handleDelete,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
    compact: isNarrow,
  });

  return (
    <div className="mcap-logs-page">
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card
            title={
              <Space>
                <FileOutlined />
                <Title level={4} style={{ margin: 0 }}>
                  MCAP Robot Log Files
                </Title>
              </Space>
            }
            extra={
              <Space wrap>
                <Segmented
                  size="small"
                  options={DENSITY_OPTIONS}
                  value={density}
                  onChange={handleDensityChange}
                />
                <Button
                  icon={<BarChartOutlined />}
                  style={BUTTON_STYLES.add}
                  onClick={openDashboard}
                >
                  Dashboard
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  style={BUTTON_STYLES.add}
                  onClick={() => setRefreshKey((key) => key + 1)}
                >
                  Refresh
                </Button>
              </Space>
            }
          >
            {/* Filters Section — on desktop one row of identity + lifecycle
                state filters, one of name lists + time windows, then the
                actions; every control writes to the URL so a filtered view can
                be shared by link (the dashboard's chart items link here). */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} lg={6}>
                <Input
                  placeholder="Search by Robot ID"
                  prefix={<SearchOutlined />}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onPressEnter={handleSearch}
                />
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Input
                  placeholder="Location"
                  value={locationText}
                  onChange={(e) => setLocationText(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  placeholder="Upload State"
                  style={{ width: "100%" }}
                  allowClear
                  value={query.upload_state}
                  onChange={handleStateChange("upload_state")}
                  options={UPLOAD_STATE_OPTIONS}
                />
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  placeholder="Processing State"
                  style={{ width: "100%" }}
                  allowClear
                  value={query.processing_state}
                  onChange={handleStateChange("processing_state")}
                  options={PROCESSING_STATE_OPTIONS}
                />
              </Col>
              <Col xs={24} sm={12} lg={4}>
                <Select
                  placeholder="Recording State"
                  style={{ width: "100%" }}
                  allowClear
                  value={query.recording_state}
                  onChange={handleStateChange("recording_state")}
                  options={RECORDING_STATE_OPTIONS}
                />
              </Col>
              <Col xs={24} sm={12} lg={5}>
                <Select
                  mode="tags"
                  placeholder="Tasks (any of)"
                  style={{ width: "100%" }}
                  allowClear
                  maxTagCount="responsive"
                  tokenSeparators={[","]}
                  value={query.tasks || []}
                  options={suggestions.tasks}
                  loading={suggestionsLoading}
                  onOpenChange={(open) => {
                    if (open) loadSuggestions();
                  }}
                  onChange={handleNamesChange("tasks")}
                />
              </Col>
              <Col xs={24} sm={12} lg={5}>
                <Select
                  mode="tags"
                  placeholder="Scene types (any of)"
                  style={{ width: "100%" }}
                  allowClear
                  maxTagCount="responsive"
                  tokenSeparators={[","]}
                  value={query.scene_types || []}
                  options={suggestions.sceneTypes}
                  loading={suggestionsLoading}
                  onOpenChange={(open) => {
                    if (open) loadSuggestions();
                  }}
                  onChange={handleNamesChange("scene_types")}
                />
              </Col>
              <Col xs={24} sm={12} lg={7}>
                <RangePicker
                  {...WINDOW_PICKER_PROPS}
                  presets={WINDOW_PRESETS}
                  style={{ width: "100%" }}
                  placeholder={["Uploaded from", "Uploaded to"]}
                  value={windowValue("uploaded_from", "uploaded_to")}
                  onChange={handleWindowChange("uploaded_from", "uploaded_to")}
                />
              </Col>
              <Col xs={24} sm={12} lg={7}>
                <RangePicker
                  {...WINDOW_PICKER_PROPS}
                  presets={WINDOW_PRESETS}
                  style={{ width: "100%" }}
                  placeholder={["Recorded from", "Recorded to"]}
                  value={windowValue("recorded_from", "recorded_to")}
                  onChange={handleWindowChange("recorded_from", "recorded_to")}
                />
              </Col>
              <Col xs={24}>
                <Space wrap>
                  <Button icon={<SearchOutlined />} onClick={handleSearch} style={BUTTON_STYLES.add}>
                    Search
                  </Button>
                  <Button
                    icon={<FilterOutlined />}
                    onClick={handleResetFilters}
                    style={BUTTON_STYLES.add}
                  >
                    Reset
                  </Button>
                </Space>
              </Col>
            </Row>

            {/* Table */}
            <div className="table-responsive">
              <Table
                columns={columns}
                dataSource={logsData}
                rowKey="_id"
                loading={loading}
                size={density}
                tableLayout="fixed"
                sticky
                expandable={{
                  expandedRowKeys,
                  onExpandedRowsChange: setExpandedRowKeys,
                  expandedRowRender: (record) => <LogRowDetail record={record} />,
                }}
                pagination={{
                  current: query.page,
                  pageSize: query.pageSize,
                  total: total,
                  showSizeChanger: true,
                  showTotal: (totalCount) => `Total ${totalCount} log files`,
                  pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
                }}
                onChange={handleTableChange}
                /* Down from a flat 1400, and it tracks the breakpoint so narrow
                   viewports don't scroll past the columns that were dropped. */
                scroll={{ x: scrollX }}
              />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Download Modal */}
      <Modal
        title={
          <Space>
            <DownloadOutlined style={{ color: "#1890ff" }} />
            <span>Download File</span>
          </Space>
        }
        open={downloadModal.visible}
        onCancel={() => setDownloadModal({ visible: false, url: "", filename: "" })}
        footer={
          <Space wrap style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button
              key="copy"
              icon={<CopyOutlined />}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(downloadModal.url);
                  notifySuccess("Download link copied to clipboard");
                } catch {
                  notifyError("Failed to copy download link");
                }
              }}
            >
              Copy Link
            </Button>
            <Button
              key="open"
              type="primary"
              icon={<DownloadOutlined />}
              href={downloadModal.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Download Link
            </Button>
            <Button
              key="close"
              onClick={() => setDownloadModal({ visible: false, url: "", filename: "" })}
            >
              Close
            </Button>
          </Space>
        }
        width={640}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text>
            Download file: <strong>{downloadModal.filename}</strong>
          </Text>
          <Text type="secondary">
            Open the link below to download the file. The link will expire in 3 hours.
          </Text>
          <div
            style={{
              padding: "8px 12px",
              background: "#fafafa",
              border: "1px solid #f0f0f0",
              borderRadius: 6,
              wordBreak: "break-all",
            }}
          >
            <Text code style={{ whiteSpace: "normal" }}>
              {downloadModal.url}
            </Text>
          </div>
        </Space>
      </Modal>

      {/* Detail Modal */}
      <Modal
        title={
          <Space>
            <FileOutlined />
            <span>MCAP Log File Details</span>
          </Space>
        }
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setSelectedLog(null);
        }}
        width={900}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setIsDetailModalVisible(false);
              setSelectedLog(null);
            }}
          >
            Close
          </Button>,
        ]}
      >
        {detailLoading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Spin size="large" />
          </div>
        ) : selectedLog ? (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Filename" span={2}>
              {selectedLog.filename}
            </Descriptions.Item>
            <Descriptions.Item label="File Size">
              {formatBytes(selectedLog.file_size)}
            </Descriptions.Item>
            <Descriptions.Item label="File Hash">
              <Text code style={{ fontSize: "11px" }}>
                {selectedLog.file_hash || "N/A"}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Robot ID">
              {selectedLog.robot_id}
            </Descriptions.Item>
            <Descriptions.Item label="Location">
              {selectedLog.location || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Tasks" span={2}>
              {selectedLog.tasks && selectedLog.tasks.length > 0
                ? selectedLog.tasks.map((task) => (
                    <Tag key={task} color="blue">
                      {task}
                    </Tag>
                  ))
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Objects" span={2}>
              {selectedLog.objects && selectedLog.objects.length > 0
                ? selectedLog.objects.map((obj) => (
                    <Tag key={obj} color="green">
                      {obj}
                    </Tag>
                  ))
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Scene Types" span={2}>
              {selectedLog.scene_types && selectedLog.scene_types.length > 0
                ? selectedLog.scene_types.map((scene) => (
                    <Tag key={scene} color="purple">
                      {scene}
                    </Tag>
                  ))
                : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Recording Start">
              {formatDateTime(selectedLog.recording_start_time, "N/A")}
            </Descriptions.Item>
            <Descriptions.Item label="Recording End">
              {formatDateTime(selectedLog.recording_end_time, "N/A")}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
              {formatDuration(selectedLog.duration_seconds)}
            </Descriptions.Item>
            <Descriptions.Item label="Message Count">
              {selectedLog.summary?.message_count?.toLocaleString() || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Channel Count">
              {selectedLog.summary?.channel_count || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Schema Count">
              {selectedLog.summary?.schema_count || 0}
            </Descriptions.Item>
            <Descriptions.Item label="Upload State">
              <UploadStateTag
                state={selectedLog.lifecycle?.upload_state || selectedLog.upload_state}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Processing State">
              <ProcessingStateTag
                state={
                  selectedLog.lifecycle?.processing_state ||
                  selectedLog.processing_state
                }
              />
            </Descriptions.Item>
            <Descriptions.Item label="Uploaded By">
              {selectedLog.uploaded_by}
            </Descriptions.Item>
            <Descriptions.Item label="Uploaded At">
              {formatDateTime(selectedLog.uploaded_at, "N/A")}
            </Descriptions.Item>
            <Descriptions.Item label="Tags" span={2}>
              {selectedLog.tags && selectedLog.tags.length > 0
                ? selectedLog.tags.map((tag) => (
                    <Tag key={tag} color="cyan">
                      {tag}
                    </Tag>
                  ))
                : "-"}
            </Descriptions.Item>
            {selectedLog.channels && selectedLog.channels.length > 0 && (
              <Descriptions.Item label="Topics/Channels" span={2}>
                <div style={{ maxHeight: "200px", overflow: "auto" }}>
                  {selectedLog.channels.map((channel) => (
                    <Tag
                      key={channel.id}
                      color="orange"
                      style={{ marginBottom: 4 }}
                    >
                      {channel.topic}
                    </Tag>
                  ))}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Text type="secondary">No details available</Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RaasRobotLogsPage;
